/**
 * Constantes partagees client / serveur.
 * Ce fichier ne doit dependre de rien (ni DOM, ni three.js, ni node).
 */

// ---------------------------------------------------------------- monde

/**
 * DEUX NOTIONS, LONGTEMPS CONFONDUES: L'ILE ET LA CARTE.
 *
 * Tant qu'il n'y a eu qu'un monde, `WORLD_SIZE` disait a la fois « le cote de
 * l'ile » et « les bornes de tout ce qui existe ». Le deuxieme monde separe les
 * deux, et c'est le seul prix de l'affaire: le monde 2 n'est pas une autre
 * carte, c'est une deuxieme ILE posee plus loin sur LE MEME plan x/z. Il y a de
 * l'eau entre les deux, donc on peut la traverser a la nage ou en barque — et
 * c'est justement parce que les deux mondes partagent un seul repere que le
 * deplacement, l'autorite serveur, la presence et les vehicules n'ont pas eu a
 * apprendre le mot « monde »: ils ne connaissent que des coordonnees.
 *
 * `WORLD_SIZE` ne designe donc plus que l'ile 1, et le monde 1 ne bouge pas
 * d'un centimetre: son terrain se genere toujours sur [0, 500]², donc tout ce
 * qui y est deja bati retrouve exactement le sol sous lequel il a ete pose.
 */

/** Cote de l'ile 1 en metres. L'ile 1 va de 0 a WORLD_SIZE sur x et z. */
export const WORLD_SIZE = 500

/** Pas de la grille de hauteur, en metres. */
export const TERRAIN_CELL = 1

/** Cote d'un chunk de terrain, en metres. Un cote d'ile en est un multiple. */
export const CHUNK_SIZE = 50

/** Nombre de chunks par cote pour l'ile 1. */
export const CHUNKS_PER_SIDE = WORLD_SIZE / CHUNK_SIZE

/**
 * L'ILE 2, et pourquoi c'est son bord OUEST qui est fige.
 *
 * Elle etait destinee a grandir beaucoup. Si on figeait son CENTRE,
 * chaque agrandissement rapprocherait son rivage du monde 1 et raccourcirait la
 * traversee, jusqu'a passer sous les trente secondes de vol demandees; et le
 * terrain se decalerait sous ce qui y est deja bati. On fige donc le bord
 * ouest: l'ile grandit vers l'EST et en ±z, l'ecart de mer devient un invariant
 * qui ne peut plus retrecir, et le sol du rivage ouest ne bouge jamais.
 *
 * L'aerodrome, pose au centre du carre d'aujourd'hui, cessera donc d'etre au
 * centre geometrique. C'est sans importance, et meme heureux: il devient un
 * aeroport cotier, c'est-a-dire exactement la ou on veut arriver quand on vient
 * du monde 1 par les airs ou par la mer.
 *
 * ET C'EST CE QUI VIENT D'ARRIVER. L'ile est passee de 200 a 700 m, avec ses
 * onze regions, en grandissant vers l'EST et vers le NORD (z0: 150 -> 0). Le
 * bord ouest n'a pas bouge d'un centimetre, donc la traversee non plus: les
 * sept cents metres de mer sont exactement les memes qu'au premier jour.
 *
 * `ILE2_COTE` est un multiple de `CHUNK_SIZE`: sinon la derniere rangee de
 * chunks deborde de la grille de hauteur et le rendu etale une jupe de terrain
 * au-dessus de l'eau.
 */
export const ILE2_X0 = 1200
export const ILE2_Z0 = 0
export const ILE2_COTE = 700

/**
 * Etendue de la CARTE, en metres: tout ce qui a des coordonnees valables.
 * Elle enveloppe les deux iles et l'ocean qui les separe. Elle se deduit de
 * l'ile 2, donc agrandir celle-ci suffit — rien d'autre a mettre a jour.
 */
export const CARTE_X = ILE2_X0 + ILE2_COTE
export const CARTE_Z = Math.max(WORLD_SIZE, ILE2_Z0 + ILE2_COTE)

/**
 * MER OUVERTE A L'OUEST DE L'ILE 1, en metres au-dela de x = 0.
 *
 * La cote ouest de l'ile 1 est a quatre metres du bord de la carte: une bande
 * d'eau ou une barque ne pouvait pas faire demi-tour, et l'ocean qu'on voit
 * au-dela etait hors d'atteinte (telephone, 13 octobre; choix de JC). Cette
 * mer ne vaut que pour ce qui va SUR L'EAU — la nage, les bateaux, le tap qui
 * vise la mer. Rien ne s'y batit, la grille de navigation ne la couvre pas, et
 * elle reste en deca de la marge d'ocean dessinee (client/render/eau.ts, 180 m).
 */
export const MER_OUEST = 150

/** Pas de la grille de navigation (A*), en metres. */
export const NAV_CELL = 2

/** Nombre de cases de navigation, sur chaque axe de la CARTE. */
export const NAV_X = Math.ceil(CARTE_X / NAV_CELL)
export const NAV_Z = Math.ceil(CARTE_Z / NAV_CELL)

/**
 * Profondeur du large, en metres: le fond entre les deux iles.
 * Le point le plus creux du monde 1 est a -12,15 m; on plonge un peu plus bas
 * pour que la haute mer soit franchement bleue et non turquoise.
 */
export const PROFONDEUR_LARGE = 15

/** Niveau de la mer en metres. Sous cette hauteur, c'est de l'eau. */
export const SEA_LEVEL = 0

/** Pente maximale marchable (montee verticale par metre horizontal). */
export const MAX_WALK_SLOPE = 0.85

/** Graine par defaut du generateur de monde. Meme graine = meme monde. */
export const WORLD_SEED = 20260805

// ---------------------------------------------------------------- temps

/** Duree d'une journee de jeu en millisecondes reelles (24 minutes). */
export const DAY_LENGTH_MS = 24 * 60 * 1000

/** Une minute reelle = une heure de jeu. */
export const MS_PER_GAME_HOUR = DAY_LENGTH_MS / 24

/** Heure de jeu au demarrage d'une nouvelle partie. */
export const START_HOUR = 8

/**
 * Bornes des phases de la journee, en heures de jeu.
 * nuit 20.5 -> 6, aube 6 -> 7.5, jour 7.5 -> 19, soir 19 -> 20.5
 */
export const PHASE_AUBE_DEBUT = 6
export const PHASE_JOUR_DEBUT = 7.5
export const PHASE_SOIR_DEBUT = 19
export const PHASE_NUIT_DEBUT = 20.5

// ---------------------------------------------------------------- simulation

/** Frequence du tick de simulation, en Hz. */
export const TICK_HZ = 20

/** Duree d'un tick en secondes. */
export const TICK_DT = 1 / TICK_HZ

/** Nombre maximum de ticks rattrapes en une frame (anti spirale de la mort). */
export const MAX_CATCHUP_TICKS = 5

// ---------------------------------------------------------------- joueur

/** Vitesse de marche en m/s. */
export const WALK_SPEED = 4

/** Vitesse de course en m/s. */
export const RUN_SPEED = 7

/** Vitesse de rotation du bonhomme, en radians par seconde. */
export const TURN_SPEED = 10

/** Rayon du bonhomme pour les collisions, en metres. */
export const PLAYER_RADIUS = 0.35

/** Hauteur du bonhomme, en metres. */
export const PLAYER_HEIGHT = 1.7

/**
 * Vitesse a la nage, en m/s, quand on est a plat. On avance nettement moins
 * vite qu'a pied. C'est le PLANCHER: la forme du nageur monte ce plafond
 * jusqu'a SWIM_SPEED_MAX (voir `vitesseNage` dans shared/sim/joueur.ts).
 */
export const SWIM_SPEED = 1.9

/**
 * Vitesse maximale a la nage, en m/s, atteinte en pleine forme.
 *
 * 2,8 est un chiffre pese, pas un chiffre rond:
 * - c'est 47 % de plus que SWIM_SPEED — cent metres de mer passent de 53 s a
 *   36 s, donc le joueur repose SENT la difference;
 * - ca reste sous la marche (4 m/s): la mer n'est jamais un raccourci, et
 *   contourner par la plage garde son interet;
 * - et surtout ca reste sous (WALK_SPEED + RUN_SPEED) / 2 = 5,5, le seuil qui
 *   fait basculer le bonhomme en etat `course` (voir `appliquerIntention`). Un
 *   nageur en `course` verrait son energie descendre trois fois plus vite, donc
 *   perdrait son elan pour l'avoir utilise: une boucle qui s'auto-annule et que
 *   rien n'expliquerait a l'ecran.
 */
export const SWIM_SPEED_MAX = 2.8

/**
 * Enfoncement du bonhomme dans l'eau, en metres.
 * Il flotte: seuls la tete et les epaules restent au-dessus.
 */
export const PROFONDEUR_NAGE = 0.95

/** Distance maximale pour recolter ou interagir, en metres. */
export const REACH = 2.2

/** Distance a laquelle on considere qu'on est arrive a destination. */
export const ARRIVE_EPSILON = 0.35

// ---------------------------------------------------------------- besoins

/**
 * Points de faim perdus par journee de jeu complete. 70 jusqu'au 13 octobre:
 * une soupe (+40) ne tenait que treize minutes reelles, et l'on passait la
 * partie a zero. JC a choisi de diviser la descente par deux.
 */
export const FAIM_PAR_JOUR = 35

/** Points d'energie perdus par journee de jeu complete. */
export const ENERGIE_PAR_JOUR = 60

/** Cout supplementaire d'energie par action de recolte. */
export const ENERGIE_PAR_RECOLTE = 0.4

/** Sous ce seuil, le bonhomme ralentit. */
export const SEUIL_BAS = 25

/** Facteur de vitesse quand faim ou energie sont au plus bas. */
export const RALENTI_MIN = 0.55

// ---------------------------------------------------------------- ressources

/** Duree de repousse d'un noeud de ressource, en jours de jeu. */
export const REPOUSSE_JOURS = 3

// ---------------------------------------------------------------- camera

/** Azimut de la camera au demarrage, en radians (45 degres). */
export const CAM_AZIMUTH = Math.PI / 4

/** Elevation de la camera, en radians (45 degres). Vue du dessus a 45. */
export const CAM_ELEVATION = Math.PI / 4

/**
 * Demi-hauteur du frustum orthographique, en metres, au zoom par defaut.
 * A 9, le bonhomme (1.7 m) fait environ 10 % de la hauteur de l'ecran: on
 * distingue ses vetements et sa coiffure, ce qui est le but d'un jeu ou on
 * habille son personnage. Plus loin, il n'etait qu'un point.
 */
export const CAM_ZOOM_DEFAULT = 9

export const CAM_ZOOM_MIN = 6
export const CAM_ZOOM_MAX = 45

/**
 * Plafond du zoom EN VOL, en metres de demi-hauteur.
 *
 * Le plafond ordinaire (45) est taille pour un bonhomme qui marche a trois
 * metres par seconde: a cette echelle-la on voit deja un quartier entier. Un
 * avion va cinq fois plus vite et se pilote en DESIGNANT un endroit ou aller —
 * il faut donc pouvoir reculer assez pour voir l'endroit avant de le toucher,
 * c'est-a-dire une bonne part du monde. A 110, on embrasse environ 390 x 220 m
 * — un bon tiers du monde — sans jamais tout afficher d'un coup: les props et
 * les noeuds de ressource s'effacent d'eux-memes quand ils tombent sous le
 * pixel utile (`reglerDetail`), donc s'ecarter COUTE MOINS cher, pas plus.
 * On ne l'accorde qu'en l'air, et le cadrage d'avant le decollage est rendu en
 * se posant: personne ne se retrouve a pied avec une camera de satellite.
 */
export const CAM_ZOOM_MAX_VOL = 110

/** Demi-hauteur du frustum quand on est dans une maison. */
export const CAM_ZOOM_INTERIEUR = 6

// ---------------------------------------------------------------- reseau / logs

/** Port du serveur. */
export const PORT = 3016

/** Intervalle d'envoi des lots de logs du client vers le serveur, en ms. */
export const LOG_FLUSH_MS = 1000

/** Nombre maximum d'entrees de log gardees en memoire avant envoi force. */
export const LOG_BUFFER_MAX = 400

/** Intervalle des instantanes de performance dans le log, en ms. */
export const LOG_PERF_MS = 15000

/** Sous ce nombre d'images par seconde, on ecrit un WARN de performance. */
export const FPS_WARN = 50
